/*
 * Copyright (c) 2023
 *
 * The source code and all documents related to the source code ("Material") are owned by
 * Frictionless Systems, LLC.
 *
 * The Material contains trade secrets, proprietary information, and confidential information.
 * No part of the Material may be used, copied, reproduced, modified, published, distributed, or
 * disclosed in any way without Frictionless Systems, LLC’s express prior written permission.
 *
 * The Material may be protected under patent, trademark, copyright, or trade secret laws.  No
 * license is granted to you by disclosure or delivery of the Materials.  Any license must be
 * express and approved by Frictionless Systems, LLC in writing.
 *
 * “Frictionless Systems” and “Impulse” are registered trademarks of Frictionless Systems, LLC.
 */

package systems.frictionless.impulse.rest.model

import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable

// This could be an inline class instead. The only reason it isn't is due to a Kotlin 1.8 compiler bug.
// that causes compile failure in ChangePasswordSection.kt.
// [#139] Is a future task to send magic links to the user, which helps solve some of the validation issues that can
// occur here.

/**
 *
 * Note that while this class normalizes to lowercase, it does not normalize some hosting-specific behaviors like
 * user@gmail.com, user+1@gmail.com, u.s.e.r@gmail.com all being the same account.
 *
 * @param emailString A valid email address meeting RFC standards, normalized to all lower case.
 */
@Serializable
data class Email private constructor(
    @SerialName("email") val emailString: String
) {
    init {
        require(EmailValidator.isValidLowercase(emailString))
        // While this should be covered by the REGEX, being explicit because the database has a hard limit due to using a varchar column
        require(emailString.length <= MAX_EMAIL_LENGTH) { "Email must be <= $MAX_EMAIL_LENGTH but is ${emailString.length}" }
    }

    companion object {
        const val MAX_EMAIL_LENGTH = 320

        fun new(emailString: String): Email {
            val normalizedEmail = emailString.lowercase()

            return Email(normalizedEmail)
        }
    }
}

object EmailValidator {
    private val EMAIL_ADDRESS_REGEX =
        Regex(
            "[a-zA-Z0-9+._%\\-]{1,256}" +
                "@" +
                "[a-zA-Z0-9][a-zA-Z0-9\\-]{0,64}" +
                "(" +
                "\\." +
                "[a-zA-Z0-9][a-zA-Z0-9\\-]{0,25}" +
                ")+"
        )

    private val LOWERCASE_EMAIL_ADDRESS_REGEX =
        Regex(
            "[a-z0-9+._%\\-]{1,256}" +
                "@" +
                "[a-z0-9][a-z0-9\\-]{0,64}" +
                "(" +
                "\\." +
                "[a-z0-9][a-z0-9\\-]{0,25}" +
                ")+"
        )

    fun isValidLowercase(emailString: String) = LOWERCASE_EMAIL_ADDRESS_REGEX.matches(emailString) && isValidHelper(emailString)

    fun isValid(emailString: String) = EMAIL_ADDRESS_REGEX.matches(emailString) && isValidHelper(emailString)

    /*
     * Additional checks not included in the regex.  These could be turned into regex in the future, but who has time
     * to figure out the syntax?
     */
    private fun isValidHelper(emailString: String): Boolean {
        if (!emailString.startsWith(".")) {
            emailString.split("@").firstOrNull()?.let { userPart ->
                if (!userPart.endsWith(".")) {
                    return true
                }
            }
        }

        return false
    }
}
